<script lang="ts" setup>
import { computed, provide, reactive } from 'vue'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { DTModalHeaderInjector } from '../../keys'
import { classArray } from '../../utils/class'
import type { ModalPlacements } from './types'
import { ModalSizes } from './types'

const props = withDefaults(defineProps<{
  modelValue: boolean
  size?: keyof typeof ModalSizes
  dialogClass?: string
  overlayClass?: string
  containerClass?: string
  initialFocus?: HTMLElement
  closeOnClickOutside?: boolean
  placement?: keyof typeof ModalPlacements
}>(), {
  size: 'md',
  closeOnClickOutside: true,
  placement: 'top',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'close'): void
}>()

const closeModal = () => {
  emit('update:modelValue', false)
  emit('close')
}

const controlledClose = () => {
  if (!props.closeOnClickOutside)
    return

  closeModal()
}

const contentClass = computed<string[]>(() => {
  const cls = [
    'flex',
    'flex-col',
    'max-h-full',
    'bg-white',
    'drop-shadow-2xl',
    'overflow-hidden',
  ]

  if (props.size !== 'fs')
    cls.push('rounded-xl')

  cls.push(ModalSizes[props.size])
  return classArray(props.dialogClass ?? '').concat(cls)
})

const overlay = computed(() => classArray(props.overlayClass ?? '').concat(['relative w-full h-full bg-black/50']))

const panelClass = computed(() => {
  const cls = ['']

  if (props.size !== 'fs')
    cls.push('p-4 pb-[88px]')

  switch (props.placement) {
    case 'top':
      cls.push('items-start justify-center')
      break
    case 'top-start':
      cls.push('items-start')
      break
    case 'top-end':
      cls.push('items-start justify-end')
      break
    case 'bottom':
      cls.push('items-end justify-center')
      break
    case 'bottom-start':
      cls.push('items-end')
      break
    case 'bottom-end':
      cls.push('items-end justify-end')
      break
    case 'left':
      cls.push('items-center')
      break
    case 'right':
      cls.push('items-center justify-end')
      break
    case 'center':
      cls.push('items-center justify-center')
      break
  }

  return classArray(props.containerClass ?? '').concat(cls)
})

const panelTransition = {
  enterActiveClass: 'transition duration-100 ease-out',
  enterFromClass: 'transform scale-95 opacity-0',
  enterToClass: 'transform scale-100 opacity-100',
  leaveActiveClass: 'transition duration-75 ease-in',
  leaveFromClass: 'transform scale-100 opacity-100',
  leaveToClass: 'transform scale-95 opacity-0',
}

const overlayTransition = {
  enter: 'ease-out duration-300',
  enterFrom: 'opacity-0',
  enterTo: 'opacity-100',
  leave: 'ease-in duration-200',
  leaveFrom: 'opacity-100',
  leaveTo: 'opacity-0',
}

provide(DTModalHeaderInjector, reactive({
  close: closeModal,
}))
</script>

<template>
  <ClientOnly>
    <TransitionRoot
      as="template"
      :show="modelValue"
    >
      <Dialog
        :open="modelValue"
        class="fixed inset-0 isolate z-[99] flex items-center justify-center"
        :initial-focus="initialFocus"
      >
        <TransitionChild
          as="template"
          v-bind="overlayTransition"
        >
          <div
            :class="overlay"
            aria-hidden="true"
          />
        </TransitionChild>
        <TransitionChild
          as="template"
          v-bind="panelTransition"
        >
          <DialogPanel
            :class="panelClass"
            class="absolute inset-0 flex"
            @click.stop="controlledClose"
          >
            <div
              :class="contentClass"
              @click.stop
            >
              <slot :close="closeModal" />
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </TransitionRoot>
  </ClientOnly>
</template>
